import { useState, useEffect, useCallback } from 'react';
import FirebaseAuthService from '../services/firebaseAuthService';
import { User } from 'firebase/auth';

interface UseAuthenticationOptimizedOptions {
    autoRefresh?: boolean;
    refreshInterval?: number;
    enableRealTime?: boolean;
    enableMFAFeature?: boolean;
    enableRiskScoring?: boolean;
    enableSessionManagement?: boolean;
    enableAuditLogging?: boolean;
    sessionTimeout?: number;
    maxSessions?: number;
}

export const useAuthenticationOptimized = (_options: UseAuthenticationOptimizedOptions = {}) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const authService = FirebaseAuthService.getInstance();
        const unsubscribe = authService.onAuthStateChange((user, _adminUser) => {
            setUser(user);
            setIsLoading(false);
            setIsAuthenticating(false);
        });

        return () => unsubscribe();
    }, []);

    const signIn = useCallback(async (email: string, password: string) => {
        setIsAuthenticating(true);
        setError(null);

        try {
            const authService = FirebaseAuthService.getInstance();
            const result = await authService.signIn(email, password);
            return result;
        } catch (error: any) {
            setError(error.message);
            return { success: false, message: error.message };
        } finally {
            setIsAuthenticating(false);
        }
    }, []);

    const signOut = useCallback(async () => {
        try {
            const authService = FirebaseAuthService.getInstance();
            await authService.signOut();
        } catch (error: any) {
            setError(error.message);
        }
    }, []);

    const isAuthenticated = useCallback(() => {
        const authService = FirebaseAuthService.getInstance();
        return authService.isAuthenticated();
    }, []);

    const isAdmin = useCallback(() => {
        const authService = FirebaseAuthService.getInstance();
        return authService.isAdmin();
    }, []);

    const getSecurityAlerts = useCallback(() => {
        // Mock security alerts
        return [
            {
                id: '1',
                type: 'info',
                message: 'System is running normally',
                timestamp: new Date().toISOString()
            }
        ];
    }, []);

    return {
        user,
        isLoading,
        isAuthenticating,
        error,
        signIn,
        signOut,
        isAuthenticated,
        isAdmin,
        getSecurityAlerts
    };
};